import { User } from "@prisma/client";
import Badge, { BadgeType } from "immigram-ui-cmon/src/components/Badge/Badge";
import Checkbox from "immigram-ui-cmon/src/components/Checkbox/Checkbox";
import {
  Icon24Chat,
  Icon24Check,
  Icon24Clock,
  Icon24Edit,
  Icon24Suit,
} from "immigram-ui-cmon/src/components/Icons";
import Paragraph from "immigram-ui-cmon/src/components/Paragraph/Paragraph";
import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import { useTranslation } from "next-i18next";
import React, { useCallback, useState } from "react";

import { ApplicationSectionStatusType } from "@/lib/applicationSectionStatus";
import { isAdmin, isDebug } from "@/lib/auth/rbac";

interface ProfessionalJourneyProjectProps<ProjectEventData> {
  id?: string;
  projectData: ProjectEventData;
  status?: ApplicationSectionStatusType | null;
  ownUser: User;
  name?: string | null;
  checkbox?: boolean;
  isValid?: boolean | null;
  showErrors?: boolean;
  sectionStatus?: ApplicationSectionStatusType;
  hasUnresolvedComments?: boolean;
  onCheckboxChange?: ({
    projectData,
    checked,
  }: {
    projectData: ProjectEventData;
    checked: boolean;
  }) => void;
  onClick?: ({ projectData }: { projectData: ProjectEventData }) => void;
}

export default function ProfessionalJourneyProject<ProjectEventData>({
  id,
  projectData,
  status,
  name,
  ownUser,
  checkbox,
  sectionStatus,
  isValid = true,
  showErrors = false,
  hasUnresolvedComments,
  onCheckboxChange,
  onClick,
}: ProfessionalJourneyProjectProps<ProjectEventData>) {
  const { t } = useTranslation();

  const [isSelected, setIsSelected] = useState(
    status === ApplicationSectionStatusType.ReviewCompleted
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<Element>) => {
      e.stopPropagation();

      onClick?.({ projectData });
    },
    [onClick, projectData]
  );

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent<Element>) => {
      e.stopPropagation();
      const nextState = !isSelected;
      setIsSelected(nextState);
      onCheckboxChange?.({ projectData, checked: nextState });
    },
    [isSelected, setIsSelected, onCheckboxChange, projectData]
  );

  const isSubmittedForReview =
    (sectionStatus || status) ===
    ApplicationSectionStatusType.SubmittedForReview;
  const isReviewCompleted =
    (sectionStatus || status) === ApplicationSectionStatusType.ReviewCompleted;

  const renderStatus = () => {
    if (!isValid && showErrors) {
      return t(
        "application:profJourney.new.ProfessionalJourneyProject.status.error"
      );
    }

    if (hasUnresolvedComments) {
      return t(
        "application:profJourney.new.ProfessionalJourneyProject.status.comments"
      );
    }

    if (isSubmittedForReview) {
      return t(
        "application:profJourney.new.ProfessionalJourneyProject.status.review"
      );
    }

    if (isReviewCompleted) {
      return t(
        "application:profJourney.new.ProfessionalJourneyProject.status.completed"
      );
    }

    return t(
      "application:profJourney.new.ProfessionalJourneyProject.status.describe"
    );
  };

  const renderIcon = () => {
    if (!isValid && showErrors) {
      return (
        <Icon24Edit
          className={clsxm(
            "flex h-8 w-8 items-center justify-center self-center rounded-2xl bg-[#6ACD82] bg-transparent text-blood-600"
          )}
        />
      );
    }

    if (hasUnresolvedComments) {
      return (
        <Icon24Chat
          className={clsxm(
            "flex h-8 w-8 items-center justify-center self-center rounded-2xl bg-[#6ACD82] bg-yellow-600 text-white-900"
          )}
        />
      );
    }

    if (isAdmin(ownUser) && isSubmittedForReview) {
      return (
        <Icon24Clock className="mr-2t flex h-6 w-6 items-center justify-center self-center text-2xl text-black-600" />
      );
    }

    if (
      isAdmin(ownUser)
        ? isReviewCompleted
        : isSubmittedForReview || isReviewCompleted
    ) {
      return (
        <Icon24Check className="flex h-8 w-8 items-center justify-center self-center rounded-2xl bg-[#6ACD82] text-white-900" />
      );
    }

    return (
      <Icon24Suit className="mr-2t flex h-6 w-6 items-center justify-center self-center text-2xl text-black-600" />
    );
  };

  return (
    <div
      className={clsxm(
        "flex flex-col gap-2 rounded-2xl border border-gray-150 px-4 py-11t text-black-900 hover:shadow-sm",
        isReviewCompleted && "border-green-50 bg-green-50"
      )}
      onClick={handleClick}
    >
      <div
        className={clsxm(
          "flex",
          isReviewCompleted &&
            !isAdmin(ownUser) &&
            "border-green-50 bg-green-50"
        )}
      >
        {checkbox ? (
          <Checkbox
            name="name"
            checked={isSelected}
            value="value"
            label={name}
            withLabel={false}
            description={renderStatus()}
            onClick={handleCheckboxClick}
          />
        ) : (
          <div className="flex flex-1 items-center justify-between gap-4 overflow-hidden">
            <div className="m-0 flex-1 overflow-hidden p-0">
              <Paragraph className="m-0 overflow-hidden text-ellipsis text-mdb text-black-900">
                {name || "Project"}
              </Paragraph>
              <Paragraph
                className={clsxm(
                  "m-0 mt-1 text-sm text-black-600",
                  !isValid && showErrors && "text-blood-600"
                )}
              >
                {renderStatus()}
              </Paragraph>
            </div>
            {renderIcon()}
          </div>
        )}
      </div>
      {isDebug(ownUser) && (
        <Badge className="self-start" size="100" type={BadgeType.Neutral}>
          {id}
        </Badge>
      )}
    </div>
  );
}
